import { Signal } from 'typed-signals';
import type { Texture } from 'pixi.js';
import { Container, NineSliceSprite, Sprite } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import type { DioramaState } from 'app/state/diorama/dioramaState';
import type { Popup } from '../../../../popups/Popup';
import { DioramaFillPopupLayout } from '../../../../../layouts/popups/DioramaFillPopupLayout';
import type { DioramaView } from '../../dioramas/DioramaView';
import { DioramaHeaderView } from '../../dioramas/DioramaHeaderView';
import { getResourceAnimationSprite } from '../../../../app/resources/animations/createResourceAnimationSprites';
import { GAME_OBJECT_POOL } from '../../../GameConstants';
import { ResourceAnimationSprite } from '../../../../app/resources/animations/ResourceAnimationSprite';
import type { Tween } from '../../../../libs/TweenManager/Tween';
import type { App } from '../../../../app/App';
import { cropSprite } from '../../utils/cropSprite';
import { waitForMs } from '../../../../utils/async-utils';
import type { PopupElementAnimationData } from '../../../../popups/PopupAnimations';
import { showDioramaCompleteFX } from './fx/showDioramaCompleteFX';

export class DioramaFillPopup implements Popup {
  private static greyButtonTexture: Texture | null = null;

  private readonly layout = new DioramaFillPopupLayout();

  public readonly root = this.layout.root;

  public readonly onEvent = new Signal<(event: string) => void>();

  private dioramaView: DioramaView | null = null;

  private readonly dioramaHeaderView: DioramaHeaderView;

  private releaseTween: Tween | null = null;

  private readonly flyingPromises: Promise<void>[] = [];

  private waitingForPieceToUnlock = false;

  private unlockedPieces = 0;

  private isFillButtonDown = false;

  constructor(private readonly app: App) {
    this.dioramaHeaderView = new DioramaHeaderView(this.layout.ribbon, app.stateController.getCurrentDioramaState());
  }

  public onAdded(data: {
    dioramaView: DioramaView;
    tilesAmount: number;
    dioramaState: DioramaState;
    isNewDiorama: boolean;
  }): void {
    this.dioramaView?.destroy();

    const { dioramaView, tilesAmount, dioramaState, isNewDiorama } = data;
    const {
      fillButton: { root: fillButton },
    } = this.layout;
    dioramaView.interactiveChildren = false;

    this.dioramaView = dioramaView.props({
      parent: this.layout.dioramaSlot,
      y: -(dioramaView.getIslandBounds().minY / 2),
    });

    this.dioramaHeaderView.updateView(dioramaState.dioramaIndex, false);

    const buttonFill = this.prepareButtonFill();

    this.releaseTween = this.createReleaseTween(tilesAmount, buttonFill);
    this.releaseTween.onComplete.add(() => {
      void this.onAllHexesReleased();
    });

    fillButton.onpointerdown = (): void => {
      if (this.waitingForPieceToUnlock) return;
      this.releaseTween?.resume();
      this.isFillButtonDown = true;
    };

    const onPointerUp = (): void => {
      this.isFillButtonDown = false;
      this.releaseTween?.pause();
    };

    this.root.tweenFrom({ alpha: 0 }, 300, 'sinOut', true);

    this.root.once('removed', () => {
      void dioramaView?.hideTitle();
    });

    if (tilesAmount > 0) {
      fillButton.visible = true;
      fillButton.onpointerup = onPointerUp;
      fillButton.onpointerupoutside = onPointerUp;
      this.layout.tileAmount.text = getLocalizedValue(tilesAmount);
    } else {
      this.root.interactiveChildren = false;
      fillButton.visible = false;
    }

    if (isNewDiorama) {
      void this.runNewDioramaSequence(tilesAmount);
      if (tilesAmount === 0) void this.onAllHexesReleased();
    }
  }

  private createReleaseTween(tilesAmount: number, buttonFill: Sprite): Tween {
    let releasedAmount = 0;

    const releaseTween = this.root.tweens.createValueTween(
      0,
      tilesAmount,
      1000,
      (value) => {
        if (!this.isFillButtonDown) return;
        const tilesToRelease = Math.round(value) - releasedAmount;
        if (tilesToRelease === 0) return;

        for (let i = 0; i < tilesToRelease; i += 1) {
          if (this.waitingForPieceToUnlock) break;
          const tileReleased = this.releaseTileParticle();
          if (tileReleased) releasedAmount += 1;
        }

        cropSprite(buttonFill, (tilesAmount - releasedAmount) / tilesAmount, 'width');
        this.layout.tileAmount.text = tilesAmount - releasedAmount;
      },
      'sinIn',
    );
    releaseTween.pause();
    return releaseTween;
  }

  private async onAllHexesReleased(): Promise<void> {
    this.root.interactiveChildren = false;
    this.isFillButtonDown = false;
    this.releaseTween?.stop();

    await Promise.all(this.flyingPromises);
    this.dioramaView?.showPieceIfHidden();

    if (this.dioramaView?.isCompleted()) {
      await this.runDioramaCompleteSequence();
      await waitForMs(3500);
    } else {
      await waitForMs(800);
      this.dioramaHeaderView.fadeOut();
    }

    this.emitFinished();
  }

  private prepareGreyButtonTexture(): Texture {
    const { greenFill } = this.layout;

    const tempContainer = new Container();
    tempContainer.addChild(
      new NineSliceSprite({
        texture: greenFill.texture,
        leftWidth: greenFill.leftWidth,
        rightWidth: greenFill.rightWidth,
        topHeight: greenFill.topHeight,
        bottomHeight: greenFill.bottomHeight,
        width: greenFill.width,
        height: greenFill.height,
      }),
    );

    const texture = this.app.renderer.generateTexture(tempContainer);

    tempContainer.destroy({ children: true });

    DioramaFillPopup.greyButtonTexture = texture;

    return texture;
  }

  private prepareButtonFill(): Sprite {
    const { greenFill, fillButton } = this.layout;

    greenFill.visible = false;

    const texture = DioramaFillPopup.greyButtonTexture ?? this.prepareGreyButtonTexture();

    return new Sprite().props({
      parent: greenFill.parent ?? fillButton.root,
      texture,
      addAbove: greenFill,
      position: greenFill.position,
    });
  }

  private releaseTileParticle(): boolean {
    const target = this.dioramaView?.getNextPieceTarget();

    if (!target) {
      this.releaseTween?.stop(true);
      return false;
    }

    if (target.isCompleted) {
      this.waitingForPieceToUnlock = true;
      this.releaseTween?.pause();
    }

    const sprite = getResourceAnimationSprite().props({
      parent: this.layout.root,
    });

    const targetLocal = this.root.toLocal(target.position);
    targetLocal.y += 40;

    this.flyingPromises.push(
      new Promise((resolve) => {
        sprite.init(
          'icon',
          this.root.toLocal(this.layout.tileIcon.getGlobalPosition()),
          targetLocal,
          150 + Math.random() * 20,
          0,
          'cubicOut',
          this.layout.tileIcon.scale.x,
          async () => {
            GAME_OBJECT_POOL.addObject(ResourceAnimationSprite, sprite);
            target.increase();
            resolve();

            if (target.isCompleted) {
              await waitForMs(100);
              this.dioramaView?.showPieceIfHidden();
              this.waitingForPieceToUnlock = false;
              this.unlockedPieces += 1;
              this.dioramaHeaderView.addPiecesToProgressBar(this.unlockedPieces);
              if (this.isFillButtonDown) {
                this.releaseTween?.resume();
              }
            }
          },
        );
      }),
    );
    return true;
  }

  private async runDioramaCompleteSequence(): Promise<void> {
    const { dioramaView } = this;
    const { popups } = this.app.controller;

    if (!dioramaView) return;

    showDioramaCompleteFX(popups.view.particleLayer, {
      x: this.app.renderer.width / 2,
      y: this.app.renderer.height / 2,
    });
    dioramaView.animateIslandFlash(600, 50, 1.35);
    popups.view.flash(250, 50);
    this.dioramaHeaderView.fadeOut();
    await dioramaView.showTitle('CONGRATULATIONS', 0.25, 700);
  }

  private async runNewDioramaSequence(tilesLeft: number): Promise<void> {
    const { dioramaHeaderView, dioramaView, layout } = this;
    const { popups } = this.app.controller;

    if (!dioramaView) return;

    dioramaHeaderView.hide();
    layout.fillButton.root.visible = false;

    if (tilesLeft === 0) {
      dioramaView.hidePieces();
      this.root.interactiveChildren = false;
    }

    popups.view.flash(500, 0);

    await waitForMs(500);
    if (layout.root.destroyed) return;

    await dioramaView?.showTitle('NEW', 0.8, 660);
    await waitForMs(1000);
    void dioramaView?.hideTitle();
    dioramaHeaderView.fadeIn();
    layout.fillButton.root.visible = true;
  }

  private emitFinished(): void {
    this.onEvent.emit('finished');
    this.onEvent.disconnectAll();
  }

  public getAnimatedElements(phase: 'in' | 'out'): PopupElementAnimationData[] {
    const { layout } = this;

    if (phase === 'in') {
      return [
        {
          container: this.dioramaView ?? new Container(),
          scale: { toX: 1, toY: 1 },
          delay: 0,
          scaleEase: 'backOut',
          duration: 500,
        },
      ];
    }

    return [{ container: layout.dioramaSlot, delay: 200 }, { container: layout.fillButton.root }];
  }
}
